import { useCallback, useRef, useState } from 'react'
import { Box, DialogContent } from '@mui/material'
import MUIDialog from '@mui/material/Dialog'
import { t } from 'i18next'
import { useMutation, useQueryClient } from 'react-query'

import PdfDialog from 'src/components/shared/PdfDialog'
import AlertDialog from 'src/components/shared/ui/AlertDialog/AlertDialog'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import ButtonSecondary from 'src/components/shared/ui/ButtonSecondary'
import DialogTitle from 'src/components/shared/ui/DialogTitle/DialogTitle'
import { CheckFillIcon, CloseRoundFullIcon } from 'src/components/shared/ui/Icons'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { SubmitStagesStyles as styles } from 'src/components/shared/ui/SubmitStagesDialog/SubmitStagesStyles'
import {
  SubmitStageStatus,
  SubmitStage,
  SubmitStageStep
} from 'src/components/shared/ui/SubmitStagesDialog/SubmitStagesTypes'

import { ERROR_MESSAGE_ALREADY_VERIFIED, ERROR_MESSAGE_NOT_VERIFY_READ } from 'src/constants/lexnet'
import { useAlertContext } from 'src/contexts/AlertContext'
import { useLexnetStatusContext } from 'src/contexts/LexnetStatusContext'
import { theme } from 'src/providers/ThemeProvider'
import * as CryptoService from 'src/services/CryptoService'
import * as MessageService from 'src/services/MessageService'
import VerificacionLectura from './view'

type Props = {
  messageId: string
  firmadoEnOrigen?: boolean
  /** El acuse ya está consolidado en LexNET: solo hay que descargar, sin volver a firmar. */
  pendienteDescarga?: boolean
}

type ErrorResponse = {
  response?: {
    code?: number
    message?: string
  }
}

type CustomError = {
  errorMessage?: string
}

export const initialStages: SubmitStage[] = [
  {
    step: SubmitStageStep.STEP_1,
    status: SubmitStageStatus.INITIAL,
    message: 'Firmando Documento',
    error: null
  },
  {
    step: SubmitStageStep.STEP_2,
    status: SubmitStageStatus.INITIAL,
    message: 'Firmando Mensaje',
    error: null
  },
  {
    step: SubmitStageStep.STEP_3,
    status: SubmitStageStatus.INITIAL,
    message: 'Firmando Token',
    error: null
  },
  {
    step: SubmitStageStep.STEP_4,
    status: SubmitStageStatus.INITIAL,
    message: 'Descargando mensaje',
    error: null
  }
]

function VerificacionLecturaDialog({ messageId, firmadoEnOrigen, pendienteDescarga }: Props): JSX.Element {
  const queryClient = useQueryClient()
  const [document, setDocument] = useState('')
  const [idEnvio, setIdEnvio] = useState('')
  const [open, setOpen] = useState(false)
  const [openSign, setOpenSign] = useState(false)
  const [openSuccess, setOpenSuccess] = useState(false)
  // const [status, setStatus] = useState('')
  const [stages, setStages] = useState(initialStages)
  const { connected, showConnectDialog } = useLexnetStatusContext()
  const { addAlert } = useAlertContext()
  const abortProcessSigin = useRef(false)

  function showErrorDialog(message: string | null = null) {
    addAlert({
      message: message || t('dialog.message.descargar_mensaje_error'),
      type: AlertType.ERROR
    })
  }

  const downloadMessageMutation = useMutation(
    async () => {
      // setStatus('Descargando mensaje...')
      return MessageService.downloadMessage(messageId)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['messageDetail', messageId])
        queryClient.invalidateQueries(['messagesListed'])
      },
      onError: () => {
        queryClient.invalidateQueries(['messageDetail', messageId])
        queryClient.invalidateQueries(['messagesListed'])
        showErrorDialog()
      },
      onSettled: () => {
        setOpen(false)
      }
    }
  )

  const verifyMutation = useMutation(async () => {
    if (!connected) {
      showConnectDialog()
      return
    }

    try {
      setOpen(true)
      const response = await MessageService.markAsReadMessage(messageId)
      setDocument(response.document)
      setIdEnvio(response.idEnvio)
    } catch (error) {
      const typedError = error as ErrorResponse
      // Number(): el código llega como entero desde la API, pero así no dependemos de ello
      const lexnetCode = Number(typedError?.response?.code)
      if (
        lexnetCode === ERROR_MESSAGE_ALREADY_VERIFIED ||
        lexnetCode === ERROR_MESSAGE_NOT_VERIFY_READ
      ) {
        downloadMessageMutation.mutate()
      } else {
        showErrorDialog(typedError?.response?.message)
        setOpen(false)
      }
    }
  })

  const signMutation = async () => {
    let currentStep = SubmitStageStep.STEP_1

    abortProcessSigin.current = false

    try {
      if (!document) {
        return
      }
      CryptoService.startTransaction()
      // setStatus('Firmando documento...')
      setStages(currentStages =>
        currentStages.map(stage =>
          stage.step === SubmitStageStep.STEP_1
            ? { ...stage, status: SubmitStageStatus.PROCESSING }
            : stage
        )
      )
      const signedDocument = await CryptoService.signPdf(document)
      const { token, messageCades } = await MessageService.markAsReadMessageSigned(
        idEnvio,
        signedDocument
      )

      setStages(currentStages =>
        currentStages.map(stage => {
          if (stage.step === SubmitStageStep.STEP_1) {
            return { ...stage, status: SubmitStageStatus.SUCCESS }
          }
          if (stage.step === SubmitStageStep.STEP_2) {
            return { ...stage, status: SubmitStageStatus.PROCESSING }
          }
          return stage
        })
      )

      let signedMessageHash = ''

      if (!abortProcessSigin.current) {
        currentStep = SubmitStageStep.STEP_2
        // setStatus('Firmando mensaje...')
        signedMessageHash = await CryptoService.signString(messageCades, true)
        setStages(currentStages =>
          currentStages.map(stage => {
            if (stage.step === SubmitStageStep.STEP_2) {
              return { ...stage, status: SubmitStageStatus.SUCCESS }
            }
            if (stage.step === SubmitStageStep.STEP_3) {
              return { ...stage, status: SubmitStageStatus.PROCESSING }
            }
            return stage
          })
        )
      }

      if (!abortProcessSigin.current) {
        currentStep = SubmitStageStep.STEP_3
        // setStatus('Firmando token...')
        const signedToken = await CryptoService.signString(token)
        CryptoService.endTransaction()
        await MessageService.makeSend(idEnvio, signedToken, signedMessageHash)
        setStages(currentStages =>
          currentStages.map(stage => {
            if (stage.step === SubmitStageStep.STEP_3) {
              return { ...stage, status: SubmitStageStatus.SUCCESS }
            }
            if (stage.step === SubmitStageStep.STEP_4) {
              return { ...stage, status: SubmitStageStatus.PROCESSING }
            }
            return stage
          })
        )
      }

      if (!abortProcessSigin.current) {
        currentStep = SubmitStageStep.STEP_4
        // setStatus('Descargando mensaje...')
        await downloadMessageMutation.mutateAsync()
        setStages(currentStages =>
          currentStages.map(stage =>
            stage.step === SubmitStageStep.STEP_4
              ? { ...stage, status: SubmitStageStatus.SUCCESS }
              : stage
          )
        )
        setOpenSign(false)
        setOpenSuccess(true)
      }
    } catch (e) {
      const typedError = e as CustomError
      setStages(currentStages =>
        currentStages.map(stage =>
          stage.step === currentStep
            ? {
                ...stage,
                status: SubmitStageStatus.ERROR,
                error: typedError?.errorMessage || t('dialog.message.descargar_mensaje_error')
              }
            : stage
        )
      )
    }
  }

  function onClose() {
    setOpen(false)
  }

  function onClickVerify() {
    if (pendienteDescarga) {
      // El acuse ya está consolidado: pedir verificación a LexNET solo devolvería
      // 20006003 "ya verificado", así que vamos directos a la descarga.
      if (!connected) {
        showConnectDialog()
        return
      }
      downloadMessageMutation.mutate()
      return
    }
    verifyMutation.mutate()
  }

  async function onClickSign() {
    setOpen(false)
    setOpenSign(true)
    setStages(initialStages)
    signMutation()
  }

  const renderStatusIcon = useCallback((status: SubmitStageStatus | null): JSX.Element | null => {
    switch (status) {
      case SubmitStageStatus.PROCESSING:
        return (
          <Box sx={styles.loader}>
            <Loader size="small" />
          </Box>
        )
      case SubmitStageStatus.SUCCESS:
        return <CheckFillIcon htmlColor={theme.colors.seaGreen100} />
      case SubmitStageStatus.ERROR:
        return <CloseRoundFullIcon htmlColor={theme.colors.persianRed100} />
      default:
        return null
    }
  }, [])

  function getClassName(status: SubmitStageStatus | null): string | undefined {
    switch (status) {
      case SubmitStageStatus.INITIAL:
        return 'initial'
      case SubmitStageStatus.PROCESSING:
        return 'processing'
      case SubmitStageStatus.SUCCESS:
        return 'success'
      case SubmitStageStatus.ERROR:
        return 'error'
      default:
        return undefined
    }
  }

  function getInfoText(stage: SubmitStage): string {
    switch (stage.status) {
      case SubmitStageStatus.PROCESSING:
        return 'Ejecutando'
      case SubmitStageStatus.SUCCESS:
        return 'Completado'
      case SubmitStageStatus.ERROR:
        return 'Error'
      default:
        return ''
    }
  }

  const documentB64 = document ? `data:application/pdf;base64,${document}` : undefined

  return (
    <>
      <PdfDialog
        actions={[
          <ButtonPrimaryOutlined key="cancel" onClick={onClose}>
            Cancelar
          </ButtonPrimaryOutlined>,
          <ButtonSecondary key="firmar" disabled={!document} onClick={onClickSign}>
            Firmar recibí
          </ButtonSecondary>
        ]}
        allowDownload={false}
        documentB64={documentB64}
        filename="Firmar recibí"
        open={open}
        onClose={onClose}
      >
        <VerificacionLectura
          onClickVerify={onClickVerify}
          firmadoEnOrigen={firmadoEnOrigen}
          pendienteDescarga={pendienteDescarga}
          downloading={downloadMessageMutation.isLoading}
        />
      </PdfDialog>

      <MUIDialog open={openSign} fullWidth maxWidth="sm" fullScreen={false} sx={styles.container}>
        <DialogTitle id="send-dialog">Firmar recibí</DialogTitle>
        <DialogContent sx={styles.content}>
          <Box component="ul">
            {stages.map(stage => (
              <Box
                component="li"
                key={stage.message}
                className={getClassName(stage.status)}
                sx={styles.stageListItem}
              >
                <Box sx={styles.row1}>
                  <Box sx={styles.statusText}>{stage.message}</Box>
                  <Box sx={styles.statusMessage} className={getClassName(stage.status)}>
                    {getInfoText(stage)}
                  </Box>
                  <Box sx={styles.statusIcon} className={getClassName(stage.status)}>
                    {renderStatusIcon(stage.status)}
                  </Box>
                </Box>
                <Box sx={styles.rowProgress}>
                  <Box sx={styles.progress} />
                </Box>
                {stage.error && (
                  <Box component="p" sx={styles.errorInfo}>
                    {stage.error}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <Box sx={styles.sendButton}>
          <ButtonPrimaryOutlined
            onClick={() => {
              abortProcessSigin.current = true
              setOpenSign(false)
              setStages(initialStages)
            }}
          >
            {t('Cancelar')}
          </ButtonPrimaryOutlined>
        </Box>
      </MUIDialog>

      <AlertDialog
        show={openSuccess}
        type={AlertType.SUCCESS}
        message={t('alert.successful_signin')}
        close={() => null}
        accept={() => null}
        cancel={() => {
          setOpenSuccess(false)
        }}
      />
    </>
  )
}

export default VerificacionLecturaDialog
